<template>
  <div class="admin-container">
    <div class="admin-header">
      <h1>Teacher List</h1>
    </div>
    <div class="border-container-unique37">
      <div class="admin-list-title">List of Teacher List</div>
      <div>
        You can view teacher, add teacher to YSA program and remove teacher from
        YSA program.
      </div>
      <div class="admin-flex-container" ref="adminFlexContainer">
        <div class="admin-search-container">
          <input
            type="text"
            class="admin-search-input"
            placeholder="Search..."
            v-model="searchQuery"
            @input="handleSearchChange"
          />
          <img
            src="/images/maker-cart/union.svg"
            alt="Search"
            class="admin-search-icon"
          />
        </div>
      </div>
      <div class="admin-list-tool-container"></div>

      <div class="mb-3" style="display: flex; gap: 10px; flex-wrap: wrap"></div>

      <TableData
        :headers="headers"
        :items="tableItems"
        :loading="tableLoading"
        :total-pages="data?.data?.total_pages || 0"
        :current-page="currentPage"
        @update:current-page="changePage"
        :is-action="true"
        :is-edit-action="false"
        :is-delete-action="false"
        @custom-action="handleCustomAction"
        @sort="handleTableSort"
        :use-api-sort="true"
        :customActions="customActions"
        :show-data-info="true"
        :total-rows="data?.data?.total_rows || 0"
        :current-limit="filters.limit"
        variant="rounded"
      />
    </div>
  </div>
</template>

<script setup>
/* eslint-disable */
import { onMounted, ref, computed, watch, inject } from "vue";
import YsaAdminService from "@/services/ysaAdminService";
import { debounce } from "lodash";
import TableData from "@/components/common/TableData.vue";
import { useUrlQuerySync } from "@/composeables/useUrlQuerySync";

const tableLoading = ref(true);
const searchQuery = ref("");
const notify = inject("notify");

const data = ref({
  message: "",
  error: null,
  data: {
    page: 1,
    limit: 10,
    total_rows: 0,
    total_pages: 0,
    rows: [],
  },
});

const currentPage = ref(1);
const filters = ref({
  search: "",
  page: 1,
  limit: 10,
  sort_by: "firstname",
  order: "asc",
});

useUrlQuerySync({
  filters,
  excludeDefaults: [{ page: 1 }, { limit: 10 }],
});

onMounted(async () => {
  await getAll();
  window.addEventListener("resize", updateLayout);
  updateLayout();
});

// Define table headers
const headers = ref([
  { text: "Teacher Name", value: "teacher_name", sortable: true },
  { text: "Email", value: "email", sortable: true },
  { text: "School", value: "school_name", sortable: true },
  { text: "Country", value: "country", sortable: true },
  { text: "Join Date", value: "date_added", sortable: true },
]);

// Map API data to table items
const tableItems = computed(() => {
  return (data.value?.data?.rows || []).map((item) => ({
    id: item.user_id,
    teacher_name: `${item.firstname || "-"} ${item.lastname || ""}`.trim(),
    email: item.email || "-",
    school_name: item.school_name || "-",
    country: item.country || "-",
    date_added: item.date_added || "-",
    isYsa: item.ysa === 1,
  }));
});

const getAll = async () => {
  try {
    tableLoading.value = true;

    const response = await YsaAdminService.getAllTeacher(filters.value);
    if (response.status === 200) {
      data.value = response.data;
    }
  } catch (error) {
    console.log(error.message);
  } finally {
    tableLoading.value = false;
  }
};

// Sync search query with filters.search
watch(
  () => filters.value.search,
  (newValue) => {
    searchQuery.value = newValue || "";
  }
);

// Debounced search handler
const handleSearchChange = debounce(() => {
  filters.value.search = searchQuery.value;
  filters.value.page = 1;
  currentPage.value = 1;
  getAll();
}, 300);

const changePage = (page) => {
  currentPage.value = page;
  filters.value.page = page;
  getAll();
};

const updateVisibilityModalDelete = () => {
  showModalDelete.value = !showModalDelete.value;
};

// Function to update layout based on window width
const updateLayout = () => {
  const container = document.querySelector(".admin-flex-container");
  const toolContainer = document.querySelector(".admin-list-tool-container");
  const parentContainer = document.querySelector(".border-container-unique37");
  const width = window.innerWidth;

  if (width <= 991) {
    parentContainer.insertBefore(toolContainer, container.nextSibling);
  } else {
    container.appendChild(toolContainer);
    container.insertBefore(toolContainer, container.children[1]);
  }
};

// Handle sorting from TableData
const handleTableSort = ({ sort_by, sort_dir }) => {
  filters.value.sort_by = sort_by === "teacher_name" ? "firstname" : sort_by;
  filters.value.order = sort_dir.toLowerCase();
  filters.value.page = 1;
  currentPage.value = 1;
  getAll();
};

// Toggle YSA membership
const toggleYSA = async (userId) => {
  const response = await YsaAdminService.toggleTeacherYSA(userId);
  if (response.status === 200) {
    notify && notify.success("Updated teacher YSA status", "Success");
    await getAll();
  }
};

// Custom actions for TableData
const customActions = ref([
  {
    type: "toggle-ysa",
    icon: (item) =>
      item.isYsa ? "/images/icon/subtract.svg" : "/images/icon/plusmark.svg",
  },
]);

const handleCustomAction = async (action) => {
  if (action.type === "toggle-ysa") {
    await toggleYSA(action.item.id);
  }
};
</script>
